import { useRef, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/unified-permissions';
import { toast } from '@/hooks/use-toast';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldX } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AICostTab } from '@/components/admin/roadmap/ai-cost/AICostTab';
import { CurriculumTab, CurriculumTabRef } from '@/components/admin/roadmap/curriculum/CurriculumTab';
import { RoadmapPreviewTab } from '@/components/admin/roadmap/preview/RoadmapPreviewTab';
import { InsightPreviewTab } from '@/components/admin/roadmap/insights/InsightPreviewTab';
import { EmailTab } from '@/components/admin/roadmap/email/EmailTab';
import { CurriculumDialog } from '@/components/admin/roadmap/curriculum/CurriculumDialog';
import { MacroCurriculum, MesoCurriculum, deleteMacroCurriculum, deleteMesoCurriculum } from '@/services/roadmap/roadmapService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import Admin from './Admin';
import FeedbackAdmin from './feedback-admin';
import BlogAdmin from './admin/BlogAdmin';
import PolicyComplianceAdmin from './admin/PolicyComplianceAdmin';
import { AdminChatPanel } from '@/features/chat/components';


const RoadmapAdmin = () => {

  const { isAdmin, permissionsStatus } = usePermissions();

  // Refs for curriculum tabs
  const macroCurriculumTabRef = useRef<CurriculumTabRef>(null);
  const mesoCurriculumTabRef = useRef<CurriculumTabRef>(null);

  // Dialog state for curriculum management
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [dialogType, setDialogType] = useState<'macro' | 'meso'>('macro');
  const [editingItem, setEditingItem] = useState<MacroCurriculum | MesoCurriculum | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'macro' | 'meso' } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // No need for useEffect - permissions are handled by the unified permissions system

  const handleEdit = (item: MacroCurriculum | MesoCurriculum, type: 'macro' | 'meso') => {
    setEditingItem(item);
    setDialogType(type);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleCreate = (type: 'macro' | 'meso') => {
    setEditingItem(null);
    setDialogType(type);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleDeleteConfirm = (id: string, type: 'macro' | 'meso') => {
    setItemToDelete({ id, type });
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      setIsDeleting(true);

      if (itemToDelete.type === 'macro') {
        await deleteMacroCurriculum(itemToDelete.id);
      } else {
        await deleteMesoCurriculum(itemToDelete.id);
      }

      toast({
        title: 'Success',
        description: `${itemToDelete.type === 'macro' ? 'Macro' : 'Meso'} curriculum entry deleted successfully.`,
      });

      setDeleteConfirmOpen(false);
      setItemToDelete(null);

      // Refresh the appropriate curriculum tab
      if (itemToDelete.type === 'macro') {
        await macroCurriculumTabRef.current?.refresh();
      } else {
        await mesoCurriculumTabRef.current?.refresh();
      }
    } catch (error) {
      console.error('Error deleting curriculum:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete curriculum entry. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (permissionsStatus === 'loading') {
    return (
      <LoadingState
        title="Verifying admin permissions"
        description="Please wait while we check your access level..."
        size="lg"
        fullScreen={true}
      />
    );
  }

  if (!isAdmin) {
    return (
      <div className="container py-10">
        <Alert variant="destructive" className="mb-6">
          <ShieldX className="h-5 w-5 mr-2" />
          <AlertDescription>
            You don't have administrator permissions to access this area.
            Please contact an admin if you believe this is an error.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Administration</h1>
        <p className="text-muted-foreground">
          Manage all administrative functions and content
        </p>
      </div>



      <Tabs defaultValue="macro-curriculum" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <TabsTrigger value="macro-curriculum">Macro Curriculum</TabsTrigger>
          <TabsTrigger value="meso-curriculum">Meso Curriculum</TabsTrigger>
          <TabsTrigger value="technique-media">Technique Media</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="chat">Chat</TabsTrigger>
          <TabsTrigger value="blog">Blog</TabsTrigger>
          <TabsTrigger value="policy-compliance">Policy</TabsTrigger>
          <TabsTrigger value="roadmap-preview">Roadmap Preview</TabsTrigger>
          <TabsTrigger value="insight-preview">Insight Preview</TabsTrigger>
          <TabsTrigger value="email-management">Email</TabsTrigger>
          <TabsTrigger value="ai-cost">AI Cost</TabsTrigger>
        </TabsList>
        
        <TabsContent value="macro-curriculum" className="mt-6">
          <CurriculumTab
            ref={macroCurriculumTabRef}
            type="macro"
            onEdit={handleEdit}
            onCreate={handleCreate}
            onDeleteConfirm={handleDeleteConfirm}
          />
        </TabsContent>

        <TabsContent value="meso-curriculum" className="mt-6">
          <CurriculumTab
            ref={mesoCurriculumTabRef}
            type="meso"
            onEdit={handleEdit}
            onCreate={handleCreate}
            onDeleteConfirm={handleDeleteConfirm}
          />
        </TabsContent>
        
        <TabsContent value="technique-media" className="mt-6">
          <Admin />
        </TabsContent>
        
        <TabsContent value="feedback" className="mt-6">
          <FeedbackAdmin />
        </TabsContent>

        <TabsContent value="chat" className="mt-6">
          <AdminChatPanel />
        </TabsContent>

        <TabsContent value="blog" className="mt-6">
          <BlogAdmin />
        </TabsContent>
        
        <TabsContent value="policy-compliance" className="mt-6">
          <PolicyComplianceAdmin />
        </TabsContent>
        
        <TabsContent value="roadmap-preview" className="mt-6">
          <RoadmapPreviewTab />
        </TabsContent>
        
        <TabsContent value="insight-preview" className="mt-6">
          <InsightPreviewTab />
        </TabsContent>
        
        <TabsContent value="email-management" className="mt-6">
          <EmailTab />
        </TabsContent>
        
        <TabsContent value="ai-cost" className="mt-6">
          <AICostTab />
        </TabsContent>
      </Tabs>

      <CurriculumDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        type={dialogType}
        initialData={editingItem || undefined}
        onSuccess={async () => {
          setDialogOpen(false);
          setEditingItem(null);
          // Refresh the appropriate curriculum tab
          if (dialogType === 'macro') {
            await macroCurriculumTabRef.current?.refresh();
          } else {
            await mesoCurriculumTabRef.current?.refresh();
          }
        }}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Curriculum Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {itemToDelete?.type} curriculum entry?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoadmapAdmin;
